import { unwrap } from "@sagot/api-client";
import type { StageStatus } from "@sagot/constants";
import { useInfiniteQuery, useMutation, useQueries } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { datedFilename, saveBlob } from "../../engage/shared/download";
import { serviceKeys } from "../service-keys";
import { useServiceMutation, useServiceQuery } from "../use-service-query";
import { PROJECT_PAGE_SIZE, type ProjectChip } from "./installation-constants";
import type { ProjectAction, ProjectCreate, ProjectFilters, ProjectPatch } from "./installation-types";

/** The server-side filter each chip stands for: projects not yet handed over, or handed over. */
function chipQuery(chip: ProjectChip) {
  return chip === "handed_over" ? ({ status: "handed_over" } as const) : ({} as const);
}

/** `GET /v1/installations` with cursor pagination. The board shows the first pages whole, so the page size is large. */
export function useProjectList(filters: ProjectFilters) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: serviceKeys.installationList(ws, { ...filters }),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.GET("/v1/installations", {
          params: { query: { q: filters.q || null, organisation_id: filters.organisationId, lead_user_id: filters.leadId, ...chipQuery(filters.chip), cursor: pageParam, limit: PROJECT_PAGE_SIZE } },
          signal,
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? null,
  });
}

/** Counts for the four figures above the board: every project, how many are being worked on, how many are ready, how many are handed over. */
export function useProjectCounts() {
  const api = useApi();
  const ws = useWorkspaceId();
  const specs = [
    { key: "all", query: {} },
    { key: "planned", query: { status: "planned" } },
    { key: "in_progress", query: { status: "in_progress" } },
    { key: "punch_list", query: { status: "punch_list" } },
    { key: "ready", query: { status: "ready_for_acceptance" } },
    { key: "handed_over", query: { status: "handed_over" } },
  ] as const;
  const results = useQueries({
    queries: specs.map((spec) => ({
      queryKey: serviceKeys.installationList(ws, { count: spec.key }),
      queryFn: ({ signal }: { signal: AbortSignal }) => unwrap(api.GET("/v1/installations", { params: { query: { limit: 1, ...spec.query } }, signal })),
      staleTime: 15_000,
    })),
  });
  return Object.fromEntries(specs.map((spec, index) => [spec.key, results[index]?.data?.total])) as Record<(typeof specs)[number]["key"], number | undefined>;
}

/** Projects of one organisation (the organisation's Projects tab). */
export function useOrganisationProjects(organisationId: string) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.installationList(ws, { organisationId }),
    request: (api, signal) => api.GET("/v1/installations", { params: { query: { organisation_id: organisationId, limit: PROJECT_PAGE_SIZE } }, signal }),
  });
}

export function useProject(projectId: string) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.installation(ws, projectId),
    request: (api, signal) => api.GET("/v1/installations/{project_id}", { params: { path: { project_id: projectId } }, signal }),
  });
}

/** The words this workspace uses (the list of open items, the project, the stage and their plurals). */
export function useInstallationVocabulary(enabled = true) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.installationPart(ws, "vocabulary", "words"),
    enabled,
    staleTime: 5 * 60_000,
    request: (api, signal) => api.GET("/v1/installations/vocabulary", { signal }),
  });
}

/** The stage templates a new project can start from (the workspace's pack ones, else the built-in one). */
export function useStageTemplates(enabled = true) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.installationPart(ws, "templates", "list"),
    enabled,
    staleTime: 5 * 60_000,
    request: (api, signal) => api.GET("/v1/installations/templates", { signal }),
  });
}

export function useCreateProject() {
  return useServiceMutation({
    request: (api, body: ProjectCreate) => api.POST("/v1/installations", { body }),
    invalidate: (ws) => [serviceKeys.installations(ws)],
  });
}

export function useUpdateProject(projectId: string) {
  return useServiceMutation({
    request: (api, body: ProjectPatch) => api.PATCH("/v1/installations/{project_id}", { params: { path: { project_id: projectId } }, body }),
    invalidate: (ws) => [serviceKeys.installations(ws)],
  });
}

export function useProjectAction(projectId: string) {
  return useServiceMutation({
    request: (api, body: ProjectAction) => api.POST("/v1/installations/{project_id}/actions", { params: { path: { project_id: projectId } }, body }),
    invalidate: (ws) => [serviceKeys.installations(ws)],
  });
}

export function useMarkReady() {
  return useServiceMutation({
    request: (api, projectId: string) => api.POST("/v1/installations/{project_id}/ready-for-acceptance", { params: { path: { project_id: projectId } } }),
    invalidate: (ws) => [serviceKeys.installations(ws)],
  });
}

export interface StageChange {
  key: string;
  status: StageStatus;
  note?: string | null;
}

export function useChangeStage(projectId: string) {
  return useServiceMutation({
    request: (api, change: StageChange) => api.POST("/v1/installations/{project_id}/stages/{stage_key}/status", { params: { path: { project_id: projectId, stage_key: change.key } }, body: { status: change.status, note: change.note ?? null } }),
    invalidate: (ws) => [serviceKeys.installations(ws)],
  });
}

export function useSaveChecklist(projectId: string) {
  return useServiceMutation({
    request: (api, change: { key: string; checklist: { text: string; done: boolean }[] }) =>
      api.PATCH("/v1/installations/{project_id}/stages/{stage_key}", { params: { path: { project_id: projectId, stage_key: change.key } }, body: { checklist: change.checklist } }),
    invalidate: (ws) => [serviceKeys.installation(ws, projectId)],
  });
}

export function useLinkProjectAsset(projectId: string) {
  return useServiceMutation({
    request: (api, assetId: string) => api.POST("/v1/installations/{project_id}/assets", { params: { path: { project_id: projectId } }, body: { asset_id: assetId } }),
    invalidate: (ws) => [serviceKeys.installations(ws), serviceKeys.assets(ws)],
  });
}

export function useUnlinkProjectAsset(projectId: string) {
  return useServiceMutation({
    request: (api, assetId: string) => api.DELETE("/v1/installations/{project_id}/assets/{asset_id}", { params: { path: { project_id: projectId, asset_id: assetId } } }),
    invalidate: (ws) => [serviceKeys.installations(ws), serviceKeys.assets(ws)],
  });
}

/** `GET /v1/installations/{id}/certificate`: the stored certificate of an accepted project, else a preview; saved as a PDF. */
export function useDownloadCertificate(projectId: string, projectNo: string) {
  const api = useApi();
  return useMutation<string, unknown, undefined>({
    mutationFn: async () => {
      const result = await api.GET("/v1/installations/{project_id}/certificate", { params: { path: { project_id: projectId } }, parseAs: "blob" });
      const blob = await unwrap(Promise.resolve(result));
      const filename = datedFilename(`certificate-${projectNo.toLowerCase()}`, "pdf");
      saveBlob(blob, filename);
      return filename;
    },
  });
}
